import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Snackbar, Alert, Slide, IconButton, Box, Typography } from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

// Create context
const ToastContext = createContext();

// Custom hook to use toast
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Slide transition
function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

// Toast Provider Component
export const ToastProvider = ({ children }) => {
  const theme = useTheme();
  const [toasts, setToasts] = useState([]);
  const [currentToast, setCurrentToast] = useState(null);

  const showToast = useCallback((message, severity = 'success', duration = 4000) => {
    const newToast = {
      id: Date.now(),
      message,
      severity,
      duration,
    };

    setToasts((prev) => [...prev, newToast]);
    // Functional update: only set if no toast is currently showing
    setCurrentToast((prev) => prev ?? newToast);
  }, []);

  const success = useCallback(
    (message, duration) => {
      showToast(message, 'success', duration);
    },
    [showToast]
  );

  const error = useCallback(
    (message, duration) => {
      showToast(message, 'error', duration);
    },
    [showToast]
  );

  const warning = useCallback(
    (message, duration) => {
      showToast(message, 'warning', duration);
    },
    [showToast]
  );

  const info = useCallback(
    (message, duration) => {
      showToast(message, 'info', duration);
    },
    [showToast]
  );

  const handleClose = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setCurrentToast(null);

    // Show next toast if any
    setTimeout(() => {
      setToasts((prev) => {
        const remaining = prev.slice(1);
        if (remaining.length > 0) {
          setCurrentToast(remaining[0]);
        }
        return remaining;
      });
    }, 100);
  }, []);

  const getIcon = (severity) => {
    switch (severity) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'success':
        return theme.palette.success.main;
      case 'error':
        return theme.palette.error.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'info':
        return theme.palette.info.main;
      default:
        return theme.palette.info.main;
    }
  };

  const contextValue = useMemo(
    () => ({ showToast, success, error, warning, info }),
    [showToast, success, error, warning, info]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {currentToast && (
        <Snackbar
          open={!!currentToast}
          autoHideDuration={currentToast.duration}
          onClose={handleClose}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{
            zIndex: 9999,
            '& .MuiSnackbarContent-root': {
              backgroundColor: 'transparent',
              padding: 0,
              overflow: 'visible',
            },
          }}
        >
          <Alert
            severity={currentToast.severity}
            onClose={handleClose}
            icon={getIcon(currentToast.severity)}
            action={
              <IconButton size="small" color="inherit" onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{
              minWidth: 300,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`
                  : `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.98)
                  : theme.palette.background.paper,
              border: `1px solid ${alpha(getSeverityColor(currentToast.severity), 0.2)}`,
              borderLeft: `4px solid ${getSeverityColor(currentToast.severity)}`,
              '& .MuiAlert-icon': {
                color: getSeverityColor(currentToast.severity),
                fontSize: '1.5rem',
              },
              '& .MuiAlert-message': {
                fontSize: '0.95rem',
                fontWeight: 500,
                color: theme.palette.text.primary,
              },
              backdropFilter: 'blur(10px)',
              animation: 'slideInRight 0.3s ease-out',
              '@keyframes slideInRight': {
                from: {
                  transform: 'translateX(100%)',
                  opacity: 0,
                },
                to: {
                  transform: 'translateX(0)',
                  opacity: 1,
                },
              },
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {currentToast.severity.charAt(0).toUpperCase() + currentToast.severity.slice(1)}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {currentToast.message}
              </Typography>
            </Box>
          </Alert>
        </Snackbar>
      )}
    </ToastContext.Provider>
  );
};

export default ToastProvider;
